import { Select } from 'components/FormUi';
import IconText from 'components/IconText';
import { Checkbox } from 'components/InputUi';
import Link from 'components/Link';
import {
	LoadingFormFieldSkeletonItem,
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import LoadMoreList from 'components/LoadMoreList';
import Popover from 'components/Popover';
import Text from 'components/Text';
import type { FieldKey } from 'hooks';
import type {
	QuestionSummary,
	Review as ReviewModel,
	ReviewScore,
	ReviewSorting,
} from 'models/api';
import type { ReviewCountPerGrade } from 'models/product';
import type { TemplateString } from 'types';
import { range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import ProductFit from '../ProductFit';

import LoadingReviewSkeleton from './LoadingReviewSkeleton';
import RatingDetails from './RatingDetails';
import RatingSummary from './RatingSummary';
import Review, { type ReviewImageClickArgs } from './Review';
import ReviewAiSummary from './ReviewAiSummary';
import TrustvoiceLink from './TrustvoiceLink';

interface Props {
	feedbackDoneText: string;
	filterSortingHeading: string;
	fit3Summary: QuestionSummary | undefined;
	flagAltText: TemplateString | FieldKey;
	hasImagesFilter: boolean;
	hasMoreReviews: boolean;
	isLoading?: boolean;
	isLoadingInitial?: boolean;
	isLoadingMoreReviews: boolean;
	isOpen: boolean;
	loadMoreButtonText: string;
	onClose: () => void;
	onHasImagesFilterChange: (hasImages: boolean) => void;
	onLoadMoreClick: () => void;
	onReviewImageClick: (args: ReviewImageClickArgs) => void;
	onScoreChange: (score: ReviewScore | undefined) => void;
	onSortOptionChange: (value: ReviewSorting | undefined) => void;
	ratingReviewCountText: TemplateString | FieldKey;
	reportButtonText: string;
	reviewAiSummary?: string;
	reviewAiSummaryDescription: string;
	reviewAiSummaryHeading: string;
	reviewCountPerGrade: ReviewCountPerGrade;
	reviewImageButtonLabel: string;
	reviewOnlyWithImagesCheckboxLabel: string;
	reviewPolicyLinkText: string;
	reviewPolicyText: string;
	reviewPolicyUrl?: string;
	reviewProductNotRecommendedText: TemplateString | FieldKey;
	reviewProductRecommendedText: TemplateString | FieldKey;
	reviews?: ReviewModel[];
	reviewScoreFilterDefaultOptionLabel: string;
	reviewScoreFilterLabel: string;
	reviewShowOriginalTextButtonText: string;
	reviewShowTranslationToLanguageButtonText: TemplateString | FieldKey;
	reviewSortingDateAscendingOptionLabel: string;
	reviewSortingDateDescendingOptionLabel: string;
	reviewSortingDefaultOptionLabel: string;
	reviewSortingLabel: string;
	reviewSortingRelevanceOptionLabel: string;
	reviewSortingScoreAscendingOptionLabel: string;
	reviewSortingScoreDescendingOptionLabel: string;
	reviewTranslatedFromLanguageText: TemplateString | FieldKey;
	reviewVerifiedBuyerLabel: string;
	score?: number;
	selectedReviewScore: ReviewScore | undefined;
	sortOption: string | undefined;
	title: string;
	totalReviews: number;
	upvoteButtonText: string;
}

export default function ReviewsPopover({
	feedbackDoneText,
	filterSortingHeading,
	fit3Summary,
	flagAltText,
	hasImagesFilter,
	hasMoreReviews,
	isLoading,
	isLoadingInitial,
	isLoadingMoreReviews,
	isOpen,
	loadMoreButtonText,
	onClose,
	onHasImagesFilterChange,
	onLoadMoreClick,
	onReviewImageClick,
	onScoreChange,
	onSortOptionChange,
	ratingReviewCountText,
	reportButtonText,
	reviewAiSummary,
	reviewAiSummaryDescription,
	reviewAiSummaryHeading,
	reviewCountPerGrade,
	reviewImageButtonLabel,
	reviewOnlyWithImagesCheckboxLabel,
	reviewPolicyLinkText,
	reviewPolicyText,
	reviewPolicyUrl,
	reviewProductNotRecommendedText,
	reviewProductRecommendedText,
	reviews,
	reviewScoreFilterDefaultOptionLabel,
	reviewScoreFilterLabel,
	reviewShowOriginalTextButtonText,
	reviewShowTranslationToLanguageButtonText,
	reviewSortingDateAscendingOptionLabel,
	reviewSortingDateDescendingOptionLabel,
	reviewSortingDefaultOptionLabel,
	reviewSortingLabel,
	reviewSortingRelevanceOptionLabel,
	reviewSortingScoreAscendingOptionLabel,
	reviewSortingScoreDescendingOptionLabel,
	reviewTranslatedFromLanguageText,
	reviewVerifiedBuyerLabel,
	score = 0,
	selectedReviewScore,
	sortOption,
	title,
	totalReviews,
	upvoteButtonText,
}: Props) {
	const { tPlural } = useI18n();

	const defaultSelectValue = 'default';
	const sortOptions: {
		label: string;
		value: ReviewSorting | typeof defaultSelectValue;
	}[] = [
		{ label: reviewSortingDefaultOptionLabel, value: defaultSelectValue },
		{ label: reviewSortingRelevanceOptionLabel, value: 'Relevance' },
		{ label: reviewSortingScoreAscendingOptionLabel, value: 'ScoreAsc' },
		{ label: reviewSortingScoreDescendingOptionLabel, value: 'ScoreDesc' },
		{ label: reviewSortingDateAscendingOptionLabel, value: 'DateAsc' },
		{ label: reviewSortingDateDescendingOptionLabel, value: 'DateDesc' },
	];
	const scoreFilterOptions: {
		disabled?: boolean;
		label: string;
		value: `${ReviewScore}` | typeof defaultSelectValue;
	}[] = [
		{ label: reviewScoreFilterDefaultOptionLabel, value: 'default' },
		{ disabled: reviewCountPerGrade.grade1 === 0, label: '1', value: '1' },
		{ disabled: reviewCountPerGrade.grade2 === 0, label: '2', value: '2' },
		{ disabled: reviewCountPerGrade.grade3 === 0, label: '3', value: '3' },
		{ disabled: reviewCountPerGrade.grade4 === 0, label: '4', value: '4' },
		{ disabled: reviewCountPerGrade.grade5 === 0, label: '5', value: '5' },
	];

	return (
		<Popover title={title} isOpen={isOpen} onClose={onClose}>
			<RatingSummary
				className="mt-4"
				reviewScore={score}
				reviewCount={totalReviews}
				reviewCountText={ratingReviewCountText}
			/>

			<RatingDetails
				className="mt-2"
				reviewCountPerGrade={reviewCountPerGrade}
				totalReviews={totalReviews}
			/>
			<IconText
				iconColor="successActive"
				icon="circle-check-outlined"
				className="mt-4"
				size="sm"
				text={
					<span>
						{reviewPolicyText}{' '}
						{reviewPolicyUrl && (
							<Link href={reviewPolicyUrl} underline>
								{reviewPolicyLinkText}
							</Link>
						)}
					</span>
				}
				as="p"
			/>

			<ProductFit className="mt-8" fit3Summary={fit3Summary} />

			<ReviewAiSummary
				className="mt-6"
				description={reviewAiSummaryDescription}
				heading={reviewAiSummaryHeading}
				summary={reviewAiSummary}
			/>

			{isLoadingInitial && (
				<LoadingSkeleton>
					<LoadingSkeletonItem height="text-h3" className="mt-12" />
					<LoadingFormFieldSkeletonItem className="mt-2" />
					<LoadingFormFieldSkeletonItem className="mt-2" />
				</LoadingSkeleton>
			)}
			{!isLoadingInitial && (
				// A product can have a review score set while the API doesn't return
				// any reviews, don't show the selects if that happens.
				<>
					<Text as="h3" text={filterSortingHeading} className="mt-12" />
					<Select
						className="mt-2 w-full"
						id="review-sort"
						label={reviewSortingLabel}
						hiddenLabel
						value={sortOption ?? defaultSelectValue}
						onChange={(e) => {
							if (e.target.value === defaultSelectValue) {
								onSortOptionChange(undefined);
							} else {
								onSortOptionChange(e.target.value as ReviewSorting);
							}
						}}
						options={sortOptions}
					/>
					<Select
						className="mt-2 w-full"
						id="review-score-filter"
						label={reviewScoreFilterLabel}
						hiddenLabel
						value={selectedReviewScore ?? defaultSelectValue}
						onChange={(e) => {
							if (e.target.value === defaultSelectValue) {
								onScoreChange(undefined);
							} else {
								onScoreChange(Number(e.target.value) as ReviewScore);
							}
						}}
						options={scoreFilterOptions}
					/>
					<Checkbox
						className="mt-6"
						id="reviews-with-images"
						label={reviewOnlyWithImagesCheckboxLabel}
						checked={hasImagesFilter}
						onChange={() => {
							onHasImagesFilterChange(!hasImagesFilter);
						}}
					/>
				</>
			)}

			{isLoading && (
				<div className="mt-6 divide-y divide-strong border-b border-strong">
					{range(2).map((index) => (
						<LoadingReviewSkeleton key={index} className="py-r-lg" />
					))}
				</div>
			)}
			{!isLoading && (
				<LoadMoreList
					onLoadMoreClick={onLoadMoreClick}
					isLoading={isLoadingMoreReviews}
					className="mt-6"
					listClassName="divide-y divide-strong border-b border-strong"
					buttonAlignment="center"
					loadedItemFocusTarget="container"
					buttonClassName="mt-6 min-w-[50%]"
					itemCountScreenReaderText={tPlural(
						'reviews_item_count_text',
						reviews?.length ?? 0,
					)}
					buttonText={loadMoreButtonText}
					hasLoadMoreButton={hasMoreReviews}
				>
					{reviews?.map((review) => (
						<Review
							key={`${review.author}-${review.date}-${review.score}`}
							className="py-r-lg"
							feedbackDoneText={feedbackDoneText}
							flagAltText={flagAltText}
							onReviewImageClick={onReviewImageClick}
							reportButtonText={reportButtonText}
							review={review}
							reviewImageButtonLabel={reviewImageButtonLabel}
							upvoteButtonText={upvoteButtonText}
							verifiedBuyerLabel={reviewVerifiedBuyerLabel}
							showOriginalTextButtonText={reviewShowOriginalTextButtonText}
							showTranslationToLanguageButtonText={
								reviewShowTranslationToLanguageButtonText
							}
							translatedFromLanguageText={reviewTranslatedFromLanguageText}
							productRecommendedText={reviewProductRecommendedText}
							productNotRecommendedText={reviewProductNotRecommendedText}
						/>
					))}
				</LoadMoreList>
			)}
			<TrustvoiceLink className="mt-6 text-center" />
		</Popover>
	);
}
ReviewsPopover.displayName = 'ReviewsPopover';
